import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Snackbar from "@material-ui/core/Snackbar";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import MuiAlert from "@material-ui/lab/Alert";
import CloseIcon from "@material-ui/icons/Close";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import VerifiedUserOutlinedIcon from "@material-ui/icons/VerifiedUserOutlined";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";

/**
 * Password confirmation dialog for destructive catalogue actions.
 *
 * A logged in session is not proof that the person at the keyboard is the
 * account holder, so actions that cannot be undone - resetting a catalogue,
 * bulk deleting a large batch of items - ask for the login password again.
 * The username and role are shown read only so it is obvious which account
 * is being used.
 *
 * The copy is supplied by the caller because the same gate protects different
 * actions; only the password mechanics live here. Verification itself happens
 * on the server inside the API that performs the action - this dialog just
 * collects the password and reports back whatever the server said.
 */

const ROLE_LABELS = {
  admin: "Admin",
  areaManager: "Area Manager",
  regionalHead: "Regional Head",
  superVendor: "Super Vendor",
};

const formatRole = (role) => {
  if (!role) return "Not available";
  if (ROLE_LABELS[role]) return ROLE_LABELS[role];
  return role
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (char) => char.toUpperCase())
    .trim();
};

const useStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: 14,
    overflow: "hidden",
  },
  header: {
    background: "linear-gradient(135deg, #2c3e50 0%, #1a252f 100%)",
    color: "#ffffff",
    padding: theme.spacing(2.5, 3),
    display: "flex",
    alignItems: "center",
    position: "relative",
  },
  headerIcon: {
    width: 46,
    height: 46,
    borderRadius: "50%",
    backgroundColor: "rgba(245, 5, 5, 0.18)",
    border: "1px solid rgba(245, 5, 5, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(2),
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: 600,
    lineHeight: 1.3,
  },
  headerSubtitle: {
    fontSize: 12.5,
    opacity: 0.75,
    marginTop: 2,
  },
  closeButton: {
    position: "absolute",
    top: 8,
    right: 8,
    color: "rgba(255, 255, 255, 0.7)",
    padding: 6,
  },
  body: {
    padding: theme.spacing(3),
    backgroundColor: "#ffffff",
  },
  warning: {
    display: "flex",
    alignItems: "flex-start",
    backgroundColor: "#fdecea",
    border: "1px solid #f8c9c4",
    borderRadius: 8,
    padding: theme.spacing(1.5, 1.75),
    marginBottom: theme.spacing(2.5),
  },
  warningIcon: {
    color: "#d93025",
    fontSize: 20,
    marginRight: theme.spacing(1.25),
    flexShrink: 0,
  },
  warningText: {
    fontSize: 12.5,
    lineHeight: 1.55,
    color: "#611a15",
  },
  identityCard: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#f6f8fa",
    border: "1px solid #e3e8ef",
    borderRadius: 10,
    padding: theme.spacing(1.5, 1.75),
    marginBottom: theme.spacing(2.5),
  },
  avatar: {
    width: 42,
    height: 42,
    backgroundColor: "#2c3e50",
    color: "#ffffff",
    fontSize: 17,
    fontWeight: 600,
    marginRight: theme.spacing(1.75),
  },
  identityLabel: {
    fontSize: 10.5,
    letterSpacing: 0.7,
    textTransform: "uppercase",
    color: "#8a94a6",
    fontWeight: 600,
  },
  identityValue: {
    fontSize: 14.5,
    fontWeight: 600,
    color: "#1f2a37",
    wordBreak: "break-all",
    lineHeight: 1.3,
  },
  roleChip: {
    marginTop: 5,
    height: 21,
    fontSize: 11,
    fontWeight: 600,
    backgroundColor: "#e7edf6",
    color: "#33506e",
  },
  prompt: {
    fontSize: 13.5,
    color: "#3c4653",
    marginBottom: theme.spacing(1.5),
    lineHeight: 1.55,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderTop: "1px solid #ebeff5",
    backgroundColor: "#fbfcfd",
  },
  cancelButton: {
    marginRight: theme.spacing(1.5),
    textTransform: "none",
    fontWeight: 600,
    color: "#5b6572",
  },
  confirmButton: {
    textTransform: "none",
    fontWeight: 600,
    minWidth: 172,
    backgroundColor: "#d93025",
    color: "#ffffff",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#b7271d",
      boxShadow: "none",
    },
    "&$confirmButtonDisabled": {
      backgroundColor: "#e9ecef",
      color: "#a6adb6",
    },
  },
  confirmButtonDisabled: {},
  buttonProgress: {
    color: "#ffffff",
    marginRight: theme.spacing(1),
  },
}));

const SecureActionAuthDialog = (props) => {
  const {
    open,
    username,
    role,
    title,
    subtitle,
    warningText,
    promptText,
    confirmLabel,
    successMessage,
    onClose,
    onConfirm,
  } = props;
  const classes = useStyles();
  const passwordRef = useRef(null);

  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [fieldError, setFieldError] = useState("");
  const [toast, setToast] = useState({
    open: false,
    severity: "error",
    message: "",
  });

  /* Every time the dialog is reopened it must start from a clean slate - a
     stale password or a stale error from the previous attempt is confusing. */
  useEffect(() => {
    if (open) {
      setPassword("");
      setShowPassword(false);
      setVerifying(false);
      setFieldError("");
    }
  }, [open]);

  const closeToast = () => setToast((current) => ({ ...current, open: false }));

  const handleClose = () => {
    if (verifying) return; // never abandon an in-flight action
    onClose();
  };

  const handleSubmit = async () => {
    if (verifying) return;

    const enteredPassword = password.trim();
    if (!enteredPassword) {
      setFieldError("Please enter your login password.");
      setToast({
        open: true,
        severity: "warning",
        message: "Please enter your login password to continue.",
      });
      return;
    }

    setVerifying(true);
    setFieldError("");

    const result = await onConfirm(enteredPassword);
    const succeeded = result && result.success;

    setVerifying(false);

    if (succeeded) {
      setPassword("");
      setToast({
        open: true,
        severity: "success",
        message: successMessage,
      });
      onClose();
      return;
    }

    const message =
      (result && result.error) ||
      "Something went wrong. Please try again.";

    setPassword("");
    setFieldError(message);
    setToast({ open: true, severity: "error", message });

    if (passwordRef.current) passwordRef.current.focus();
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    }
  };

  const avatarInitial = username ? username.charAt(0).toUpperCase() : "?";

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        disableBackdropClick={verifying}
        disableEscapeKeyDown={verifying}
        classes={{ paper: classes.paper }}
        onEntered={() => {
          if (passwordRef.current) passwordRef.current.focus();
        }}
        aria-labelledby="secure-action-auth-title"
      >
        <div className={classes.header}>
          <div className={classes.headerIcon}>
            <VerifiedUserOutlinedIcon style={{ color: "#ff6b60" }} />
          </div>
          <div>
            <Typography
              id="secure-action-auth-title"
              className={classes.headerTitle}
            >
              {title}
            </Typography>
            <Typography className={classes.headerSubtitle}>
              {subtitle}
            </Typography>
          </div>
          <IconButton
            className={classes.closeButton}
            onClick={handleClose}
            disabled={verifying}
            aria-label="close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <div className={classes.body}>
          <div className={classes.warning}>
            <WarningRoundedIcon className={classes.warningIcon} />
            <Typography className={classes.warningText}>
              {warningText}
            </Typography>
          </div>

          <div className={classes.identityCard}>
            <Avatar className={classes.avatar}>{avatarInitial}</Avatar>
            <div style={{ minWidth: 0 }}>
              <Typography className={classes.identityLabel}>
                Logged in as
              </Typography>
              <Typography className={classes.identityValue}>
                {username || "Not available"}
              </Typography>
              <Chip
                size="small"
                label={formatRole(role)}
                className={classes.roleChip}
              />
            </div>
          </div>

          <Typography className={classes.prompt}>{promptText}</Typography>

          <TextField
            inputRef={passwordRef}
            fullWidth
            variant="outlined"
            size="small"
            label="Login Password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              if (fieldError) setFieldError("");
            }}
            onKeyPress={handleKeyPress}
            error={Boolean(fieldError)}
            helperText={fieldError || " "}
            disabled={verifying}
            autoComplete="current-password"
            inputProps={{ maxLength: 64 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon
                    fontSize="small"
                    style={{ color: "#8a94a6" }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    size="small"
                    tabIndex={-1}
                    onClick={() => setShowPassword((visible) => !visible)}
                    aria-label={showPassword ? "hide password" : "show password"}
                  >
                    {showPassword ? (
                      <VisibilityOffIcon fontSize="small" />
                    ) : (
                      <VisibilityIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>

        <div className={classes.actions}>
          <Button
            className={classes.cancelButton}
            onClick={handleClose}
            disabled={verifying}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            classes={{
              root: classes.confirmButton,
              disabled: classes.confirmButtonDisabled,
            }}
            onClick={handleSubmit}
            disabled={verifying || !password.trim()}
          >
            {verifying ? (
              <React.Fragment>
                <CircularProgress
                  size={16}
                  className={classes.buttonProgress}
                />
                Verifying...
              </React.Fragment>
            ) : (
              confirmLabel
            )}
          </Button>
        </div>
      </Dialog>

      {/* Rendered outside the Dialog so the success toaster survives the
          dialog closing once the action goes through. */}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={toast.open}
        autoHideDuration={4000}
        onClose={closeToast}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={toast.severity}
          onClose={closeToast}
        >
          {toast.message}
        </MuiAlert>
      </Snackbar>
    </React.Fragment>
  );
};

SecureActionAuthDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  username: PropTypes.string,
  role: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  warningText: PropTypes.node,
  promptText: PropTypes.node,
  confirmLabel: PropTypes.string,
  successMessage: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  /* Receives the entered password, resolves to { success, error } */
  onConfirm: PropTypes.func.isRequired,
};

SecureActionAuthDialog.defaultProps = {
  username: "",
  role: "",
  title: "Confirm It's You",
  subtitle: "Password required to continue",
  warningText: "This action cannot be undone.",
  promptText: "To continue, please enter your login password.",
  confirmLabel: "Confirm",
  successMessage: "Action completed successfully.",
};

export default SecureActionAuthDialog;
